import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Amplify from "@aws-amplify/core";
import ChatLauncher from "./chat-launcher";

import * as realTimeApi from "../../real-time-api-config";
import { defaultChatBotSettings } from "../../consts/settings";
import { useAppInfo, useQuery } from "../../helpers/hooks/common-hook";

import VoiceModePlugin from "../../pages/voice-mode";
import PlayAudio from "../../components/play-audio-v2";
import { ReplyWaitIndicator } from "../question-ask/conv-message/reply-wait-indicator";
import {
  createBotMessagePayload,
  createSelfMessagePayload,
} from "../../helpers/utils/message-utils";
import { getConfig } from "../../services/config";

import ChatConversation from "./chat-conversation";
import ChatHeader from "./chat-header";
import ChatFooter from "./chat-footer";
import ChatForm from "./chat-form";
import {AUTH_KEY} from "../../consts/common-consts";
import {useCookies} from "react-cookie";

const { customAlphabet } = require("nanoid");
const customNanoid = customAlphabet("1234567890abcdefghikkl", 18);

Amplify.configure(realTimeApi.config);

function getNewSessionId() {
  return customNanoid(15);
}

function getRequestAttribute(requestAttributesParam) {
  try {
    return JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(requestAttributesParam), character => character.charCodeAt(0))));
  } catch (e) {
    return {}
  }
}

const dummyMessages = [];

function ChatPluginTask({ onChangeInspectData, voiceModeChangeNotifyMessage }) {
  const [ cookies ] = useCookies(["cookie-name"]);
  const query = useQuery();
  const { appId, accessKey } = useAppInfo();

  const plugin = query.get("plugin");
  const preview = query.get("preview") === "1";
  const voiceParam = query.get("voice");
  const sessionIdParam = query.get("session_id");
  const requestAttributesParams = getRequestAttribute(query.get("request_attributes"));

  const [sessionId, setSessionId] = useState();
  const [botReply, setBotReply] = useState();
  const [botReplyWait, setBotReplyWait] = useState(false);
  const [messages, setMessages] = useState(dummyMessages || []);
  const [voiceAudioSource, setVoiceAudioSource] = useState();
  const [initReconnect, setInitReconnect] = useState({});

  const [formState, setFormState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: "",
  });

  // if a plugin, by default, initiated with close state
  const [close, setClose] = useState(Boolean(plugin));

  const [voiceMode, setVoiceMode] = useState(!!voiceParam);

  const [widgetReady, setWidgetReady] = useState(false);
  const previewParentOrigin = useRef(null);

  const [chatBotSetting, setChatBotSetting] = useState(defaultChatBotSettings);

  const [settingsState, setSettingsState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: "",
  });

  useEffect(() => {
    setSessionId(sessionIdParam ||  getNewSessionId())
  }, [sessionIdParam]);
  
  // Commit the launcher/chat DOM before the host resizes its iframe. Otherwise
  // the full chat panel briefly paints into the launcher's 64px viewport.
  useLayoutEffect(() => {
    if (!plugin) return;
    document.documentElement.dataset.chatWidget = close ? "closed" : "open";
    return () => { delete document.documentElement.dataset.chatWidget; };
  }, [plugin, close]);

  useLayoutEffect(() => {
    if (!plugin || !widgetReady || window.parent === window) return;
    const origin = previewParentOrigin.current || '*';
    window.parent.postMessage({ type: 'botcircuits_widget_layout', isOpen: !close,
      position: chatBotSetting.position === 'bottom-center' ? 'bottom-center' : 'bottom-right' }, origin);
    window.parent.postMessage({ type: 'botcircuits_toggle_state', isOpen: !close }, origin);
    if (preview && previewParentOrigin.current) {
      window.parent.postMessage({ type: 'botcircuits_preview_applied', isOpen: !close }, origin);
    }
  }, [plugin, widgetReady, close, chatBotSetting, preview]);

  // Listen for messages from parent window
  useEffect(() => {
    const handleParentMessage = (event) => {
      // Check if the message is from the parent window
      if (event.source === window.parent) {
        if (preview && event.data?.type === 'botcircuits_preview_settings' && event.data.settings) {
          previewParentOrigin.current = event.origin === 'null' ? '*' : event.origin;
          setChatBotSetting({ ...defaultChatBotSettings, ...event.data.settings });
          setWidgetReady(true);
        }
        // Handle toggle command from parent
        if (event.data && event.data.type === 'botcircuits_parent_toggle') {
          if (plugin && typeof event.data.isOpen === "boolean") setClose(!event.data.isOpen);
        }
      }
    };
    
    // Add event listener
    window.addEventListener('message', handleParentMessage);
    if (preview) window.parent.postMessage({ type: 'botcircuits_preview_ready' }, '*');
    
    // Clean up
    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, [plugin, preview]);

  useEffect(() => {
    if (!preview) fetchChatBotSettings();
  }, []);

  useEffect(() => {
    if (!preview && sessionId && messages.length === 0) {
      const question = "Hi";
      const requestAttributes = {};
      sendMessageToBot(question, requestAttributes, false);
    }
  }, [sessionId, messages.length]);

  useEffect(() => {
    if (chatBotSetting.title) {
      document.title = chatBotSetting.title;

      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.getElementsByTagName("head")[0].appendChild(link);
      }
      link.href = chatBotSetting.logo;
    }
  }, [chatBotSetting]);

  useEffect(() => {
    if (voiceModeChangeNotifyMessage) {
      console.log("voiceModeChangeNotifyMessage", voiceModeChangeNotifyMessage);
      setVoiceMode(voiceModeChangeNotifyMessage.status);
    }
  }, [voiceModeChangeNotifyMessage]);

  useEffect(() => {
    console.log("sessionId change", sessionId);
  }, [sessionId]);

  const updateSettingsState = (processing, success, failed, errorMessage) => {
    setSettingsState(
      Object.assign({}, { processing, success, failed, errorMessage })
    );
  };

  const fetchChatBotSettings = async () => {
    updateSettingsState(true, false, false, "");
    try {
      const resp = await getConfig(`app_${appId}`, "web_integration", accessKey);
      if (resp && resp.data) {
        setChatBotSetting({ ...defaultChatBotSettings, ...resp.data });
        updateSettingsState(false, true, false, "");
      } else {
        updateSettingsState(false, false, true, "Failed to load settings");
      }
    } catch (error) {
      updateSettingsState(false, false, true, "Failed to load settings");
    } finally {
      setWidgetReady(true);
    }
  };

  const onClickToggleClose = (state) => {
    // if not a plugin. close effect will not affect
    if (!plugin) return;

    setClose(state);
    

  };

  const initSubscription = () => {
    const subscription = realTimeApi.subscribeBotMessage(
      accessKey,
      appId,
      sessionId,
      ({ data }) => {
        data = JSON.parse(data);
        setBotReply(data);
        setBotReplyWait(false);
        onChangeInspectData(data);

        if (data.message.voiceMessage) {
          setVoiceAudioSource(data.message.voiceMessage);
        }
      },
      (error) => {
        if (error.error.errors[0].message === "Connection closed") {
          console.error("CONN FAILED", error);
          setInitReconnect({});
        }
      }
    );
    console.log("subscription", subscription);
    return () => {
      console.log("unsubscribe......");
      subscription.unsubscribe();
    };
  };

  const updateFormState = (processing, success, failed, errorMessage) => {
    setFormState(
      Object.assign({}, { processing, success, failed, errorMessage })
    );
  };

  function getAuthCookieValue() {
    try {
      const cookieString = document.cookie;
      const cookies = cookieString.split('; ');

      for (let cookie of cookies) {
        const [cookieName, cookieValue] = cookie.split('=');
        if (cookieName === AUTH_KEY) {
          return JSON.parse(decodeURIComponent(cookieValue));
        }
      }
    } catch (e) {}
    return null;
  }

  const sendMessageToBot = async (
    reqQuestion,
    requestAttributes = {},
    appendToUI = true
  ) => {
    if (preview) return;
    if (!formState.processing) {
      if (appendToUI) {
        appendMessage(createSelfMessagePayload(requestAttributes.title || reqQuestion));
      }
      updateFormState(true, false, false);
      setBotReplyWait(true);

      const savedAuthData = getAuthCookieValue()
      if (savedAuthData) {
        requestAttributes['auth'] = savedAuthData
      }

      try {
        await realTimeApi.sendUserMessage(
          accessKey,
          appId,
          sessionId,
          JSON.stringify({
            action: "executor",
            appId,
            sessionId,
            inputText: reqQuestion,
            requestAttributes: { ...requestAttributes, ...requestAttributesParams },
          })
        );
        updateFormState(false, true, false);
      } catch (error) {
        updateFormState(false, false, true, "Unable to send message");
        setBotReplyWait(false);
        throw error;
      }
    }
  };

  const handleOnAskSubmit = async (question, requestAttributes = {}) => {
    await sendMessageToBot(question, requestAttributes);
  };

  const onInvokeAction = async (payload, requestAttributes = {}) => {
    await sendMessageToBot(payload, requestAttributes);
  };

  const onChangeVoiceInvokeState = async (state) => {
    const { processing, success, failed } = state;
    setBotReplyWait(processing);
  };

  const appendMessage = (message) => {
    const newMessages = [...messages, message];
    setMessages(newMessages);
  };

  useEffect(() => {
    if (!preview && sessionId) {
      return initSubscription();
    }
  }, [sessionId]);

  useEffect(() => {
    console.log("initReconnect");
    if (!preview && sessionId) {
      return initSubscription();
    }
  }, [initReconnect]);

  useEffect(() => {
    if (botReply) {
      appendMessage(createBotMessagePayload(botReply.message));
    }
  }, [botReply]);

  const toggleVoiceMode = () => {
    setVoiceMode((prevMode) => !prevMode);
    setSessionId(sessionIdParam || getNewSessionId()); // Reset session ID
    setMessages([]); // Clear messages
    setBotReply(null); // Clear bot reply
  };

  if (close) {
    return <ChatLauncher settings={chatBotSetting} onOpen={() => onClickToggleClose(false)} />;
  }

  if (voiceMode) {
    return (
      <VoiceModePlugin
        appId={appId}
        accessKey={accessKey}
        sessionId={sessionId}
        voiceAudioSource={voiceAudioSource}
        botReplyIn={botReply}
        onInvokeAction={onInvokeAction}
        settingsState={settingsState}
        chatBotSetting={chatBotSetting}
        toggleVoiceMode={toggleVoiceMode}
      />
    );
  }

  return (
    <div className="chat-shell" style={{ backgroundColor: chatBotSetting.chatWindowBackgroundColor }}>
      <div className="plugin-chat-wrapper flex h-full min-h-0 flex-1 flex-col">
        {/* HEADER */}
        <ChatHeader
          settingsState={settingsState}
          chatBotSetting={chatBotSetting}
          toggleVoiceMode={toggleVoiceMode}
          appId={appId}
          accessKey={accessKey}
          sessionId={sessionId}
          voiceAudioSource={voiceAudioSource}
          botReplyIn={botReply}
          onInvokeAction={onInvokeAction}
          onClose={()=>onClickToggleClose(true)}
        />
        {!voiceAudioSource && (
          <>
            <PlayAudio audioSourceData={voiceAudioSource} botReply={botReply} />
          </>
        )}
        {/* CHAT MESSAGER */}
        <div className="w-full max-w-screen-md mx-auto flex min-h-0 flex-1 overflow-hidden">
          {/* min-w-0 on both: these are flex items, and a flex item defaults to
              min-width:auto ("never shrink below the content"). Without it a
              wide message - a scrolling card row, a long code block - stretches
              the whole chat column past the viewport instead of scrolling
              inside it. */}
          <div className="plugin-chat-view flex flex-1 flex-col justify-between min-w-0">
            <div className="chat-message-wrapper scrollbars">
              {chatBotSetting.welcomeMessage && (
                <div className="chat-message"><div className="chat-message-row">
                  <div className="chat-message-avatar"><img src={chatBotSetting.logo || defaultChatBotSettings.logo} alt="Assistant" /></div>
                  <div className="chat-message-content"><p className="whitespace-pre-wrap">{chatBotSetting.welcomeMessage}</p></div>
                </div></div>
              )}
              {messages.map((message, idx) => {
                return (
                  <ChatConversation
                    key={idx}
                    disabled={formState.processing}
                    sessionId={sessionId}
                    chatBotSetting={chatBotSetting}
                    onInvokeAction={onInvokeAction}
                    {...message}
                  />
                );
              })}
              {botReplyWait && (
                <ReplyWaitIndicator chatBotSetting={chatBotSetting} />
              )}
            </div>

            {/* INPUT FORM */}
            <ChatForm
              disabled={preview || formState.processing}
              onChangeInspectData={onChangeInspectData}
              onHandleSubmit={handleOnAskSubmit}
              chatBotSetting={chatBotSetting}
              sessionId={sessionId}
            />
          </div>
        </div>
        {/* FOOTER */}
        <ChatFooter chatBotSetting={chatBotSetting} />
      </div>
    </div>
  );
}

ChatPluginTask.propTypes = {
  onChangeInspectData: PropTypes.func,
  voiceModeChangeNotifyMessage: PropTypes.shape({
    status: PropTypes.bool,
  }),
};

export default ChatPluginTask;
